/**
 * Notifications Screen
 * 
 * Centralized view of all system notifications:
 * - Uber income gaps
 * - Accounting discrepancies
 * - Data quality issues
 * - Missing data warnings
 * - System alerts
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import theme from '../../theme';
import {
  getAllNotifications,
  getNotificationSummary,
  getCriticalNotifications,
} from '../../api/notificationDetection';
import {
  Notification,
  NotificationSummary,
  NotificationSeverity,
  NotificationCategory,
} from '../../types/notifications';

export function NotificationsScreen() {
  const navigation = useNavigation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'critical' | 'unread'>('all');
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<NotificationCategory | 'all'>('all');

  const isWeb = Platform.OS === 'web';

  // Load notifications
  const loadNotifications = async () => {
    try {
      setLoading(true);
      const [notifs, summaryData] = await Promise.all([
        getAllNotifications(),
        getNotificationSummary(),
      ]);
      setNotifications(notifs);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadNotifications();
  };

  // Filter notifications
  const getFilteredNotifications = () => {
    let filtered = [...notifications];

    // Apply severity filter
    if (activeFilter === 'critical') {
      filtered = filtered.filter(n => n.severity === 'critical' || n.severity === 'high');
    } else if (activeFilter === 'unread') {
      filtered = filtered.filter(n => !n.isRead);
    }

    // Apply category filter
    if (activeCategoryFilter !== 'all') {
      filtered = filtered.filter(n => n.category === activeCategoryFilter);
    }

    return filtered;
  };

  const filteredNotifications = getFilteredNotifications();

  // Severity styling
  const getSeverityColor = (severity: NotificationSeverity) => {
    switch (severity) {
      case 'critical': return theme.colors.red600;
      case 'high': return theme.colors.orange600;
      case 'medium': return theme.colors.yellow600;
      case 'low': return theme.colors.blue600;
      case 'info': return theme.colors.gray500;
      case 'success': return theme.colors.green600;
      default: return theme.colors.gray400;
    }
  };

  const getSeverityIcon = (severity: NotificationSeverity) => {
    switch (severity) {
      case 'critical': return 'alert-circle';
      case 'high': return 'warning';
      case 'medium': return 'information-circle';
      case 'low': return 'information-circle-outline';
      case 'info': return 'information';
      case 'success': return 'checkmark-circle';
      default: return 'notifications';
    }
  };

  const getCategoryLabel = (category: NotificationCategory) => {
    const labels: Record<NotificationCategory, string> = {
      uber_gaps: 'Uber Gaps',
      accounting_issues: 'Accounting',
      data_quality: 'Data Quality',
      constraint_violations: 'Constraints',
      missing_data: 'Missing Data',
      system: 'System',
      payment_due: 'Payments',
      maintenance_due: 'Maintenance',
    };
    return labels[category] || category;
  };

  const getCategoryIcon = (category: NotificationCategory) => {
    const icons: Record<NotificationCategory, string> = {
      uber_gaps: 'car',
      accounting_issues: 'calculator',
      data_quality: 'analytics',
      constraint_violations: 'shield-checkmark',
      missing_data: 'alert-circle-outline',
      system: 'settings',
      payment_due: 'cash',
      maintenance_due: 'construct',
    };
    return icons[category] || 'notifications';
  };

  // Render summary cards
  const renderSummaryCards = () => {
    if (!summary) return null;

    return (
      <View style={{
        flexDirection: isWeb ? 'row' : 'column',
        gap: 12,
        marginBottom: 20,
      }}>
        {/* Total Notifications */}
        <View style={{
          flex: 1,
          backgroundColor: theme.colors.white,
          padding: 16,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: theme.colors.gray200,
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: theme.colors.primary + '15',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Ionicons name="notifications" size={20} color={theme.colors.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 24, fontWeight: '600', color: theme.colors.textPrimary }}>
                {summary.total}
              </Text>
              <Text style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                Total Notifications
              </Text>
            </View>
          </View>
        </View>

        {/* Unread */}
        <View style={{
          flex: 1,
          backgroundColor: theme.colors.white,
          padding: 16,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: theme.colors.gray200,
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: theme.colors.blue600 + '15',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Ionicons name="mail-unread" size={20} color={theme.colors.blue600} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 24, fontWeight: '600', color: theme.colors.textPrimary }}>
                {summary.unread}
              </Text>
              <Text style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                Unread
              </Text>
            </View>
          </View>
        </View>

        {/* Critical */}
        <View style={{
          flex: 1,
          backgroundColor: theme.colors.white,
          padding: 16,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: theme.colors.red600 + '30',
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <View style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: theme.colors.red600 + '15',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Ionicons name="alert-circle" size={20} color={theme.colors.red600} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 24, fontWeight: '600', color: theme.colors.red600 }}>
                {summary.bySeverity.critical + summary.bySeverity.high}
              </Text>
              <Text style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                Critical Issues
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  // Render filter buttons
  const renderFilters = () => {
    return (
      <View style={{ marginBottom: 16 }}>
        {/* Severity Filters */}
        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
          <TouchableOpacity
            onPress={() => setActiveFilter('all')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 20,
              backgroundColor: activeFilter === 'all' ? theme.colors.primary : theme.colors.gray100,
              borderWidth: 1,
              borderColor: activeFilter === 'all' ? theme.colors.primary : theme.colors.gray200,
            }}
          >
            <Text style={{
              fontSize: 13,
              fontWeight: '500',
              color: activeFilter === 'all' ? theme.colors.white : theme.colors.gray600,
            }}>
              All ({notifications.length})
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setActiveFilter('critical')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 20,
              backgroundColor: activeFilter === 'critical' ? theme.colors.red600 : theme.colors.gray100,
              borderWidth: 1,
              borderColor: activeFilter === 'critical' ? theme.colors.red600 : theme.colors.gray200,
            }}
          >
            <Text style={{
              fontSize: 13,
              fontWeight: '500',
              color: activeFilter === 'critical' ? theme.colors.white : theme.colors.gray600,
            }}>
              Critical ({summary?.bySeverity.critical || 0})
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setActiveFilter('unread')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 20,
              backgroundColor: activeFilter === 'unread' ? theme.colors.blue600 : theme.colors.gray100,
              borderWidth: 1,
              borderColor: activeFilter === 'unread' ? theme.colors.blue600 : theme.colors.gray200,
            }}
          >
            <Text style={{
              fontSize: 13,
              fontWeight: '500',
              color: activeFilter === 'unread' ? theme.colors.white : theme.colors.gray600,
            }}>
              Unread ({summary?.unread || 0})
            </Text>
          </TouchableOpacity>
        </View>

        {/* Category Filters */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {['all', 'uber_gaps', 'accounting_issues', 'data_quality', 'missing_data'].map((cat) => (
              <TouchableOpacity
                key={cat}
                onPress={() => setActiveCategoryFilter(cat as any)}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 16,
                  backgroundColor: activeCategoryFilter === cat ? theme.colors.primary + '15' : theme.colors.gray50,
                  borderWidth: 1,
                  borderColor: activeCategoryFilter === cat ? theme.colors.primary : theme.colors.gray200,
                }}
              >
                <Text style={{
                  fontSize: 12,
                  fontWeight: '500',
                  color: activeCategoryFilter === cat ? theme.colors.primary : theme.colors.gray600,
                }}>
                  {cat === 'all' ? 'All Categories' : getCategoryLabel(cat as NotificationCategory)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>
    );
  };

  // Render notification item
  const renderNotificationItem = (notification: Notification) => {
    const severityColor = getSeverityColor(notification.severity);
    const severityIcon = getSeverityIcon(notification.severity);

    return (
      <TouchableOpacity
        key={notification.id}
        style={{
          backgroundColor: notification.isRead ? theme.colors.gray50 : theme.colors.white,
          padding: 16,
          borderRadius: 12,
          marginBottom: 12,
          borderLeftWidth: 4,
          borderLeftColor: severityColor,
          borderWidth: 1,
          borderColor: theme.colors.gray200,
          ...Platform.select({
            web: {
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            },
          }),
        }}
        onPress={() => {
          // Mark as read and navigate if action URL exists
          if (notification.actionUrl) {
            // Navigation logic here
            console.log('Navigate to:', notification.actionUrl);
          }
        }}
      >
        <View style={{ flexDirection: 'row', gap: 12 }}>
          {/* Severity Icon */}
          <View style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            backgroundColor: severityColor + '15',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Ionicons name={severityIcon as any} size={18} color={severityColor} />
          </View>

          {/* Content */}
          <View style={{ flex: 1 }}>
            {/* Title & Category Badge */}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4, gap: 8 }}>
              <Text style={{
                fontSize: 15,
                fontWeight: '600',
                color: theme.colors.textPrimary,
                flex: 1,
              }}>
                {notification.title}
              </Text>
              <View style={{
                paddingHorizontal: 8,
                paddingVertical: 3,
                borderRadius: 10,
                backgroundColor: theme.colors.gray100,
              }}>
                <Text style={{
                  fontSize: 10,
                  fontWeight: '500',
                  color: theme.colors.gray600,
                  textTransform: 'uppercase',
                }}>
                  {getCategoryLabel(notification.category)}
                </Text>
              </View>
            </View>

            {/* Message */}
            <Text style={{
              fontSize: 14,
              color: theme.colors.textSecondary,
              marginBottom: 8,
              lineHeight: 20,
            }}>
              {notification.message}
            </Text>

            {/* Footer: Timestamp & Action */}
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{
                fontSize: 12,
                color: theme.colors.gray400,
              }}>
                {new Date(notification.timestamp).toLocaleString()}
              </Text>

              {notification.actionLabel && (
                <TouchableOpacity
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 6,
                    backgroundColor: theme.colors.primary,
                  }}
                >
                  <Text style={{
                    fontSize: 12,
                    fontWeight: '500',
                    color: theme.colors.white,
                  }}>
                    {notification.actionLabel}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={{ marginTop: 12, color: theme.colors.textSecondary }}>
          Loading notifications...
        </Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.gray50 }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: isWeb ? 24 : 16 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {/* Summary Cards */}
        {renderSummaryCards()}

        {/* Filters */}
        {renderFilters()}

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <View style={{
            backgroundColor: theme.colors.white,
            padding: 40,
            borderRadius: 12,
            alignItems: 'center',
            borderWidth: 1,
            borderColor: theme.colors.gray200,
          }}>
            <Ionicons name="checkmark-circle-outline" size={64} color={theme.colors.green600} />
            <Text style={{
              fontSize: 18,
              fontWeight: '600',
              color: theme.colors.textPrimary,
              marginTop: 16,
            }}>
              All Clear!
            </Text>
            <Text style={{
              fontSize: 14,
              color: theme.colors.textSecondary,
              marginTop: 8,
              textAlign: 'center',
            }}>
              No {activeFilter === 'all' ? '' : activeFilter} notifications at this time
            </Text>
          </View>
        ) : (
          <View>
            {filteredNotifications.map(renderNotificationItem)}
          </View>
        )}
      </ScrollView>
    </View>
  );
}
